/**
 * @file    useQuestionsCalculations.ts
 * @scope   SHARED
 * @summary LEGACY-ONLY. Owns the question and calculation pickers: available options, the user's
 *          selection, the mutual filtering between both lists and their drag & drop reordering.
 * @consumes Guardrails chart config payload, `customWidget.customWidgetConfig`
 * @produces Option lists, selections, filtered lists, change/drag handlers and the `touchedQC` flag
 * @sideEffects None beyond its own local state; the caller persists the selection to the store.
 * @notes Questions and calculations live in a SINGLE hook on purpose: selecting a question removes
 *        its calculations from the other picker and vice versa, so splitting them would require
 *        each half to reach into the other's state — a worse coupling than keeping one cohesive
 *        "measure selection" domain.
 */
import { useCallback, useEffect, useState } from 'react';
import { SelectOptions } from 'core-ui/MultipleSelectSortable/MultipleSelectSortable';
import { Calculation } from 'core-ui/GroupSelectSortable/GroupSelectSortable';
import { arrayMove } from 'helpers/sorting';
import {
  groupCalculationsByQuestion,
  mapCalculationsToOptions,
  mapQuestionsToOptions,
} from '../utils/questionCalculationMapping';

/** Everything the drawer needs from the question/calculation domain. */
export type UseQuestionsCalculationsResult = {
  /** All questions offered by the current chart. */
  chartQuestions: SelectOptions[] | undefined;
  /** All calculations offered by the current chart, grouped by question. */
  chartCalculations: SelectOptions[];
  /** Questions picked by the user, in display order. */
  selectedQuestions: SelectOptions[];
  /** Calculations picked by the user, in display order. */
  selectedCalculations: Calculation[];
  /** Questions still selectable (those whose calculations are not already selected). */
  filteredQuestions: SelectOptions[];
  /** Calculations still selectable (those whose question is not already selected). */
  filteredCalculations: SelectOptions[];
  /** Handler for the questions picker. */
  handleQuestionChange: (newValue: SelectOptions[]) => void;
  /** Handler for the calculations picker. */
  handleCalculationChange: (newValue: Calculation[]) => void;
  /** dnd-kit handler that reorders the selected questions. */
  handleQuestionsDragEnd: (event: any) => void;
  /** dnd-kit handler that reorders the selected calculations. */
  handleCalculationsDragEnd: (event: any) => void;
  /** Parses a Guardrails chart config into both option lists and restores the saved selection. */
  parseQuestionsAndCalculations: (chartData: any) => void;
  /** Clears both selections (used when the chart type changes). */
  clearSelections: () => void;
  /** `true` once the user has interacted with either picker — gates the validation messages. */
  touchedQC: boolean;
  /** Marks the pickers as touched. */
  setTouchedQC: React.Dispatch<React.SetStateAction<boolean>>;
};

/**
 * @param customWidget Widget currently being edited (source of the persisted selection).
 */
export const useQuestionsCalculations = (customWidget: any): UseQuestionsCalculationsResult => {
  const [chartQuestions, setChartQuestions] = useState<SelectOptions[]>();
  const [chartCalculations, setChartCalculations] = useState<SelectOptions[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<SelectOptions[]>([]);
  const [selectedCalculations, setSelectedCalculations] = useState<Calculation[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<SelectOptions[]>([]);
  const [filteredCalculations, setFilteredCalculations] = useState<SelectOptions[]>([]);
  const [touchedQC, setTouchedQC] = useState<boolean>(false);

  /**
   * Mutual exclusion, half 1: a question already selected hides its calculation group.
   * WHY: picking both would ask Guardrails for the same measure twice.
   */
  useEffect(() => {
    if (selectedQuestions) {
      const newCalculations = chartCalculations.filter((c) =>
        selectedQuestions.every((q) => q.label !== c.label)
      );
      setFilteredCalculations(newCalculations);
      return;
    }
    setFilteredCalculations(chartCalculations);
  }, [selectedQuestions, chartCalculations]);

  /** Mutual exclusion, half 2: a selected calculation hides the question it measures. */
  useEffect(() => {
    if (selectedCalculations) {
      const newQuestions = chartQuestions?.filter((q) =>
        selectedCalculations.every((c) => c.questionName !== q.value)
      );
      setFilteredQuestions(newQuestions || []);
      return;
    }
    setFilteredQuestions(chartQuestions || []);
  }, [selectedCalculations, chartQuestions]);

  /**
   * Rebuilds both option lists from a Guardrails chart config and restores whatever the widget
   * had already saved. Calculations are parsed first so the grouped list is ready before the
   * question list triggers the mutual-filtering effects.
   */
  const parseQuestionsAndCalculations = (chartData: any) => {
    const questionOptions = mapQuestionsToOptions(
      chartData?.questions?.length ? chartData.questions : [],
      true
    );

    // Calculations.
    if (chartData?.calculations) {
      setChartCalculations(groupCalculationsByQuestion(chartData));
      if (customWidget?.customWidgetConfig?.calculations?.length) {
        setSelectedCalculations(
          mapCalculationsToOptions(customWidget.customWidgetConfig.calculations)
        );
      }
    } else {
      setChartCalculations([]);
    }

    // Questions.
    setChartQuestions(questionOptions);
    if (customWidget?.customWidgetConfig?.questions?.length) {
      setSelectedQuestions(mapQuestionsToOptions(customWidget.customWidgetConfig.questions));
    }
  };

  const clearSelections = () => {
    if (selectedQuestions && selectedQuestions?.length > 0) setSelectedQuestions([]);
    if (selectedCalculations && selectedCalculations?.length > 0) setSelectedCalculations([]);
  };

  const handleQuestionChange = (newValue: SelectOptions[]) => {
    if (newValue) setSelectedQuestions(newValue);
  };

  const handleCalculationChange = (newValue: Calculation[]) => {
    if (newValue) setSelectedCalculations(newValue);
  };

  // WHY: the selection order is meaningful (it drives the column/series order in the widget), so
  // drag & drop reorders the array instead of just toggling membership.
  const handleQuestionsDragEnd = useCallback((event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSelectedQuestions((current) => {
        const oldIndex = current.findIndex((i) => i.value === active.id);
        const newIndex = current.findIndex((i) => i.value === over.id);
        return arrayMove(current, oldIndex, newIndex);
      });
    }
  }, []);

  const handleCalculationsDragEnd = useCallback((event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSelectedCalculations((current) => {
        const oldIndex = current.findIndex((i) => i.value === active.id);
        const newIndex = current.findIndex((i) => i.value === over.id);
        return arrayMove(current, oldIndex, newIndex);
      });
    }
  }, []);

  return {
    chartQuestions,
    chartCalculations,
    selectedQuestions,
    selectedCalculations,
    filteredQuestions,
    filteredCalculations,
    handleQuestionChange,
    handleCalculationChange,
    handleQuestionsDragEnd,
    handleCalculationsDragEnd,
    parseQuestionsAndCalculations,
    clearSelections,
    touchedQC,
    setTouchedQC,
  };
};
